import { AppText } from "@toothfairy/shared-ui";
import AppSecureTextInput from "@toothfairy/shared-ui/AppSecureTextInput";
import AppSwitch from "@toothfairy/shared-ui/AppSwitch";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { View } from "react-native";
import { MIN_MONTHLY_CAP_USD, formatUsd } from "../../Constants/billing";

// SpendCapControl: account-level monthly maximum spend (USD). null/0 = no cap.
// Hard stop across ALL the user's workspaces for the current billing cycle.
//
// Pure editor: the slider edits a LOCAL draft and reports it via onDraftChange.
// Saving is handled by the parent (one shared Save button). The slider's
// `seed` (initialValue) is only re-synced from `value` when the incoming value
// differs from the current draft — so the parent echoing back the same draft
// does NOT reseed mid-drag (which would loop AppSecureTextInput's reset).
const SpendCapControl = ({ value, onDraftChange, style }) => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const savedNumeric = Number(value) || 0;
  const savedEnabled = savedNumeric > 0;

  const [enabled, setEnabled] = useState(savedEnabled);
  const [draft, setDraft] = useState(
    savedNumeric > 0 ? savedNumeric : MIN_MONTHLY_CAP_USD,
  );
  // seed drives the slider's initialValue (what the thumb shows). It is only
  // updated when the SAVED value differs from what the slider currently shows —
  // so it stays stable during a drag (value doesn't change mid-drag => no
  // reseed => no AppSecureTextInput reset loop) but DOES update after a save so
  // the thumb reflects the persisted value.
  const [seed, setSeed] = useState(
    savedNumeric > 0 ? savedNumeric : MIN_MONTHLY_CAP_USD,
  );

  // Reseed when the saved value differs from the displayed seed. This fires
  // after a save (value becomes the new number) and on a real load, but NOT
  // during a drag (value is stable while dragging).
  useEffect(() => {
    const incoming = savedNumeric > 0 ? savedNumeric : MIN_MONTHLY_CAP_USD;
    if (incoming !== seed) {
      setEnabled(savedEnabled);
      setSeed(incoming);
      setDraft(incoming);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const report = (nextEnabled, nextDraft) => {
    onDraftChange?.(nextEnabled ? nextDraft : null);
  };

  return (
    <View
      style={[
        {
          padding: 12,
          backgroundColor: currentTheme?.theme?.softBackgroundColor,
          borderRadius: 10,
          marginTop: 8,
        },
        style,
      ]}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <AppText style={{ fontWeight: "bold", flex: 1, flexWrap: "wrap" }}>
          {t("credits.spendCap.title")}
        </AppText>
        <AppSwitch
          isTicked={enabled}
          onChange={(v) => {
            setEnabled(v);
            report(v, draft);
          }}
          wrapperStyle={{ paddingVertical: 0 }}
        />
      </View>
      <AppText
        style={{
          fontSize: 12,
          color:
            currentTheme?.theme?.secondary_font_color ||
            currentTheme?.theme?.font_color,
          marginTop: 4,
        }}
      >
        {t("credits.spendCap.description")}
      </AppText>

      {enabled && (
        <View style={{ marginTop: 12 }}>
          <AppText style={{ fontSize: 13, marginBottom: 4 }}>
            {t("credits.spendCap.amountLabel")}
          </AppText>
          <AppSecureTextInput
            key={`spendcap-${seed}`}
            inputType="SLIDER"
            showTextInputLabel={false}
            isEditable
            showEditButtons={false}
            initialValue={seed}
            onSliderValueChange={(v) => {
              const val = Math.max(MIN_MONTHLY_CAP_USD, Number(v) || 0);
              setDraft(val);
              report(enabled, val);
            }}
            minimumValue={MIN_MONTHLY_CAP_USD}
            maximumValue={10000}
            step={5}
            unitLabel={t("credits.units.usd", { defaultValue: "$" })}
            wrapperStyle={{ width: "100%" }}
            coreViewStyle={{ minHeight: 40 }}
          />
          <AppText
            style={{
              fontSize: 11,
              color: currentTheme?.theme?.secondary_font_color,
              marginTop: 4,
            }}
          >
            {t("credits.spendCap.usdHint", { amount: formatUsd(MIN_MONTHLY_CAP_USD) })}
          </AppText>
          <AppText
            style={{
              fontSize: 11,
              color: currentTheme?.theme?.orange,
              marginTop: 6,
            }}
          >
            {t("credits.spendCap.pauseNotice")}
          </AppText>
        </View>
      )}
    </View>
  );
};

export default SpendCapControl;
